import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Star as StarIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useFilmSubscription } from '../hooks/useFilmSubscription';
import { brandColors } from '../theme/theme';
import FilmPricingTable from '../components/Subscription/FilmPricingTable';
import { FILM_TIER_NAMES } from '../lib/filmSubscriptionTiers';
import { getUserCompany } from '../lib/services/enterpriseCompanyService';
import { createCheckoutSession } from '../lib/services/stripeService';

/**
 * FilmSubscriptionManagement Page
 * 
 * Main subscription management page for Film product
 */
const FilmSubscriptionManagement = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const {
    filmTier,
    filmStatus,
    filmExpiresAt,
    enterpriseCompanyId,
    isEnterpriseAdmin,
    isEnterpriseMember
  } = useFilmSubscription();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkoutTier, setCheckoutTier] = useState(null);
  const [checkoutError, setCheckoutError] = useState(null);

  useEffect(() => {
    const fetchCompanyData = async () => {
      if (enterpriseCompanyId) {
        try {
          const companyData = await getUserCompany(currentUser.uid);
          setCompany(companyData);
        } catch (err) {
          console.error('Error fetching company:', err);
          setError('Failed to load company information');
        }
      }
      setLoading(false);
    };

    fetchCompanyData();
  }, [enterpriseCompanyId, currentUser]);

  const handleUpgrade = async (tier) => {
    try {
      setCheckoutError(null);
      setCheckoutTier(tier);

      const origin = window?.location?.origin || 'https://app.greenlightanalytics.com';
      const successUrl = `${origin}/subscription/film?status=success&tier=${tier}`;
      const cancelUrl = `${origin}/subscription/film?status=cancelled`;

      const checkoutUrl = await createCheckoutSession(currentUser.uid, tier, 'monthly', {
        successUrl,
        cancelUrl
      });

      if (checkoutUrl) {
        window.location.assign(checkoutUrl);
        return;
      }

      throw new Error('Stripe session did not return a redirect URL');
    } catch (upgradeError) {
      console.error('Error starting upgrade checkout:', upgradeError);
      setCheckoutError(upgradeError.message || 'Unable to start checkout. Please try again.');
    } finally {
      setCheckoutTier(null);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(-1)}
          sx={{ mb: 2 }}
        >
          Back
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Film Subscription
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your Film Tracking subscription
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Current Plan */}
      <Paper elevation={3} sx={{ p: 4, mb: 4, border: `2px solid ${brandColors.primary}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
              Your Current Plan
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Chip
                icon={<StarIcon />}
                label={FILM_TIER_NAMES[filmTier]}
                color="primary"
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  height: 36,
                  bgcolor: brandColors.primary,
                  color: 'white'
                }}
              />
              <Chip
                label={filmStatus || 'Active'}
                color={filmStatus === 'active' ? 'success' : 'default'}
                size="small"
              />
            </Box>
          </Box>
        </Box>

        <Divider sx={{ my: 3 }} />

        {/* Plan Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filmExpiresAt && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarIcon sx={{ color: 'text.secondary' }} />
              <Typography variant="body1">
                <strong>Expires:</strong> {formatDate(filmExpiresAt)}
              </Typography>
            </Box>
          )}

          {isEnterpriseMember && company && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon sx={{ color: 'text.secondary' }} />
                <Typography variant="body1">
                  <strong>Company:</strong> {company.companyName}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  <strong>Seat Usage:</strong> {company.usedSeats} of {company.totalSeats} seats used
                </Typography>
              </Box>
              {isEnterpriseAdmin && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => navigate('/enterprise/company/dashboard')}
                  >
                    Manage Enterprise Users
                  </Button>
                </Box>
              )}
            </>
          )}

          {isEnterpriseMember && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Your subscription is managed by your enterprise company. Contact your company administrator to make changes.
            </Alert>
          )}
        </Box>
      </Paper>

      {/* Pricing Table - Only show if not enterprise member */}
      {!isEnterpriseMember && (
        <>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
            Upgrade Your Plan
          </Typography>
          {checkoutError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {checkoutError}
            </Alert>
          )}
          <FilmPricingTable
            currentTier={filmTier}
            onUpgrade={handleUpgrade}
            processingTier={checkoutTier}
          />

          {/* Additional Info */}
          <Paper sx={{ p: 3, mt: 4, bgcolor: '#f5f5f5' }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Need Help?
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Upgrades take effect immediately
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • Downgrades take effect at the end of your current billing period
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              • For enterprise plans, please contact sales@greenlightanalytics.com
            </Typography>
            <Typography variant="body2" color="text.secondary">
              • Questions? Email support@greenlightanalytics.com
            </Typography>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default FilmSubscriptionManagement;

